import {
  getNativeDeviceToken
} from './nativeDeviceAuth';

import {
  getHonorsTestingContext
} from './systemTime';


const SUPABASE_URL =
  process.env
    .REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env
    .REACT_APP_SUPABASE_PUBLISHABLE_KEY;


const TUNGHAI_INSTITUTION_ID =
  '09a27111-2043-4dd7-a696-668d3dd778aa';


async function isConfirmedTunghaiStudent(
  session
) {
  /*
   * Preserve Master Student testing.
   */
  if (
    session?.isMaster ===
    true
  ) {
    return true;
  }

  const studentId =
    String(
      session?.studentId ||
      ''
    ).trim();

  if (!studentId) {
    return false;
  }

  try {
    const response =
      await fetch(
        `${SUPABASE_URL}/rest/v1/students?id=eq.${encodeURIComponent(
          studentId
        )}&select=institution_id&limit=1`,
        {
          headers: {
            apikey:
              SUPABASE_KEY,

            Authorization:
              'Bearer ' +
              SUPABASE_KEY
          }
        }
      );

    if (!response.ok) {
      console.error(
        'Could not verify student institution for Honors access:',
        response.status,
        await response.text()
      );

      /*
       * Fail closed:
       * if we cannot confirm Tunghai,
       * do not expose Honors signup.
       */
      return false;
    }

    const rows =
      await response.json();

    const institutionId =
      Array.isArray(rows)
        ? String(
            rows[0]
              ?.institution_id ||
            ''
          ).trim()
        : '';

    return (
      institutionId ===
      TUNGHAI_INSTITUTION_ID
    );

  } catch (error) {
    console.error(
      'Could not verify student institution for Honors access:',
      error
    );

    return false;
  }
}


function gateHonorsPortalState(
  portalState,
  canUseTunghaiHonorsSignup
) {
  if (
    !portalState ||
    canUseTunghaiHonorsSignup
  ) {
    return portalState;
  }

  /*
   * Preserve any status information,
   * but never expose either signup action.
   */
  return {
    ...portalState,

    program: {
      ...(
        portalState.program ||
        {}
      ),

      showApplication:
        false
    },

    courseRegistration: {
      ...(
        portalState.courseRegistration ||
        {}
      ),

      show:
        false,

      open:
        false
    }
  };
}


async function callHonorsSignups(
  session,
  action,
  payload = {}
) {
  if (!session) {
    throw new Error(
      'A student session is required.'
    );
  }


  const body = {
    action,
    ...payload
  };

  /*
   * This value is only a request from the client.
   * honors-signups independently authenticates the student
   * and must ignore it unless the authenticated account is
   * the Master Student.
   */
  if (
    session.isMaster ===
    true
  ) {
    const testing =
      getHonorsTestingContext(
        session
      );


    if (testing) {
      body.testing =
        testing;
    }
  }



  if (
    session.nativeDeviceSession ===
      true
  ) {
    const deviceToken =
      await getNativeDeviceToken();

    if (!deviceToken) {
      throw new Error(
        'A saved mobile login is required.'
      );
    }

    body.deviceToken =
      deviceToken;

  } else {
    const webSessionToken =
      String(
        session.webSessionToken ||
        ''
      ).trim();

    if (!webSessionToken) {
      throw new Error(
        'Your student web session is no longer available. Please sign in again.'
      );
    }

    body.webSessionToken =
      webSessionToken;
  }


  const response =
    await fetch(
      `${SUPABASE_URL}/functions/v1/honors-signups`,
      {
        method: 'POST',

        headers: {
          apikey:
            SUPABASE_KEY,

          Authorization:
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json'
        },

        body:
          JSON.stringify(
            body
          )
      }
    );


  const text =
    await response.text();


  let result =
    null;


  try {
    result =
      text
        ? JSON.parse(text)
        : null;

  } catch {
    throw new Error(
      text ||
      'The Tunghai Honors Program service returned an invalid response.'
    );
  }


  if (
    !response.ok ||
    result?.ok === false
  ) {
    throw new Error(
      result?.message ||
      result?.error ||
      'The Tunghai Honors Program request failed.'
    );
  }


  return result;
}


export async function
getStudentHonorsPortalState(
  session
) {
  const [
    result,
    canUseTunghaiHonorsSignup
  ] =
    await Promise.all([
      callHonorsSignups(
        session,
        'portalState'
      ),

      isConfirmedTunghaiStudent(
        session
      )
    ]);

  return gateHonorsPortalState(
    result?.portalState ||
      null,

    canUseTunghaiHonorsSignup
  );
}


export function
isCurrentHonorsProgramMember(
  portalState
) {
  return (
    portalState
      ?.program
      ?.status ===
    'Honors'
  );
}


export async function
createStudentHonorsFormHandoff(
  session,
  formType
) {
  const canUseTunghaiHonorsSignup =
    await isConfirmedTunghaiStudent(
      session
    );

  if (
    !canUseTunghaiHonorsSignup
  ) {
    throw new Error(
      formType ===
        'application'
        ? 'The Tunghai Honors Program application is available only to Tunghai University students.'
        : 'Honors course pre-registration is available only to Tunghai University students.'
    );
  }

  const result =
    await callHonorsSignups(
      session,
      'createHandoff',
      {
        formType
      }
    );

  if (
    !result?.handoff?.token
  ) {
    throw new Error(
      'The form handoff could not be created.'
    );
  }


  return result.handoff;
}



const HONORS_CMS_WEB_URL =
  'https://tunghai-honors-cms.web.app';


export async function
createStudentHonorsFormUrl(
  session,
  formType
) {
  const handoff =
    await createStudentHonorsFormHandoff(
      session,
      formType
    );


  const url =
    new URL(
      handoff.formPath,
      HONORS_CMS_WEB_URL
    );


  url.searchParams.set(
    'embedded',
    '1'
  );

  url.searchParams.set(
    'handoff',
    handoff.token
  );


  if (
    formType ===
      'course_registration'
  ) {
    url.searchParams.set(
      'hssBridge',
      SUPABASE_URL +
      '/functions/v1/honors-signups'
    );
  }


  return url.toString();
}
